import React, { useState, useEffect } from 'react';
import { fetchPumps, savePump, deletePump, fetchTanks, fetchProducts } from '../../services/api';

const initialSetups = [
  { id: 1, pumpNo: 'P-01', name: 'Road Side Pump 1', tank: 'T-02 (HSD)', product: 'HSD', meterType: 'Digital', installDate: '01-04-2020', lastCalib: '01-04-2025', status: 'Active' },
  { id: 2, pumpNo: 'P-02', name: 'Road Side Pump 2', tank: 'T-01 (MS)',  product: 'MS',  meterType: 'Digital', installDate: '01-04-2020', lastCalib: '01-04-2025', status: 'Active' },
  { id: 3, pumpNo: 'P-03', name: 'Tank Side Pump 1', tank: 'T-02 (HSD)', product: 'HSD', meterType: 'Digital', installDate: '15-06-2021', lastCalib: '15-06-2024', status: 'Active' },
  { id: 4, pumpNo: 'P-04', name: 'Tank Side Pump 2', tank: 'T-01 (MS)',  product: 'MS',  meterType: 'Analog',  installDate: '15-06-2021', lastCalib: '15-06-2024', status: 'Inactive' },
  { id: 5, pumpNo: 'P-05', name: 'CNG Pump 1',       tank: 'T-03 (CNG)', product: 'CNG', meterType: 'Digital', installDate: '10-01-2022', lastCalib: '10-01-2025', status: 'Active' },
  { id: 6, pumpNo: 'P-06', name: 'CNG Pump 2',       tank: 'T-03 (CNG)', product: 'CNG', meterType: 'Digital', installDate: '10-01-2022', lastCalib: '10-01-2025', status: 'Active' },
];

export default function PumpSetup({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [setups, setSetups] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    pumpNo: '',
    name: '',
    tank: '',
    product: '',
    meterType: 'Digital',
    installDate: '',
    lastCalib: '',
    status: 'Active'
  });

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [pumpsRes, tanksRes, prodsRes] = await Promise.all([
        fetchPumps(companyId),
        fetchTanks(companyId),
        fetchProducts(companyId)
      ]);

      if (pumpsRes.success) {
        const raw = pumpsRes.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        setSetups(list.map(p => ({
          id: p.Pumpid,
          pumpNo: p.PumpName,
          name: p.PumpName,
          tank: p.ConnectedTank || '',
          product: p.ProductName || '',
          meterType: p.MeterType || 'Digital',
          installDate: p.InstallDate || '',
          lastCalib: p.LastCalibration || '',
          status: p.Status || 'Active'
        })));
      }
      if (tanksRes.success) setTanks(tanksRes.Response || []);
      if (prodsRes.success) setProducts(prodsRes.Response || []);
    } catch (err) {
      console.error("Pump setup load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [companyId]);

  const openAdd = () => {
    setEditId(null);
    setForm({
      pumpNo: '',
      name: '',
      tank: tanks.length > 0 ? `${tanks[0].TankName}` : 'T-01 (MS)',
      product: products.length > 0 ? products[0].Name : 'MS',
      meterType: 'Digital',
      installDate: '',
      lastCalib: '',
      status: 'Active'
    });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({ ...s });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.pumpNo || !form.name) return;

    try {
      const payload = {
        Companyid: companyId,
        PumpName: form.name || form.pumpNo,
        ConnectedTank: form.tank,
        ProductName: form.product,
        MeterType: form.meterType,
        InstallDate: form.installDate,
        LastCalibration: form.lastCalib,
        Status: form.status,
        IDS: editId ? String(editId) : "0"
      };

      const result = await savePump(payload);
      if (result.success || result.Response === "UPDATED" || result.Response === "INSERTED") {
        await loadInitialData();
        setShowForm(false);
      } else {
        alert(result.message || "Failed to save pump");
      }
    } catch (err) {
      console.error("Save pump error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pump setup?')) return;
    try {
      const result = await deletePump(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadInitialData();
      } else {
        alert(result.message || "Failed to delete pump");
      }
    } catch (err) {
      console.error("Delete pump error:", err);
    }
  };

  return (
    <div className="pump-page">

      <div className="page-header">
        <h2 className="page-title">Pump/Pump Setup</h2>
        <button className="btn btn-primary btn-sm" onClick={openAdd}>
          ＋ Add Pump Setup
        </button>
      </div>

      {showForm && (
        <div className="section-card">

          <div className="section-header">
            {editId ? 'Edit Pump Setup' : 'New Pump Setup'}
          </div>

          <div className="form-row">

            <div className="form-group">
              <label>Pump No.</label>
              <input className="form-control"
                value={form.pumpNo}
                onChange={e => setForm({ ...form, pumpNo: e.target.value })}
                placeholder="P-07"
              />
            </div>

            <div className="form-group">
              <label>Pump Name</label>
              <input className="form-control"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Road Side Pump 3"
              />
            </div>
            <div className="form-group">
              <label>Connected Tank</label>
              <select className="form-control"
                value={form.tank}
                onChange={e => setForm({ ...form, tank: e.target.value })}
              >
                {tanks.map(t => (
                  <option key={t.Tankid}>{t.TankName}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Product</label>
              <select className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                {products.map(p => (
                  <option key={p.Productid}>{p.Name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Meter Type</label>
              <select className="form-control"
                value={form.meterType}
                onChange={e => setForm({ ...form, meterType: e.target.value })}
              >
                <option>Digital</option>
                <option>Analog</option>
              </select>
            </div>

            <div className="form-group">
              <label>Install Date</label>
              <input className="form-control"
                value={form.installDate}
                onChange={e => setForm({ ...form, installDate: e.target.value })}
                placeholder="DD-MM-YYYY"
              />
            </div>

            <div className="form-group">
              <label>Last Calibration</label>
              <input className="form-control"
                value={form.lastCalib}
                onChange={e => setForm({ ...form, lastCalib: e.target.value })}
                placeholder="DD-MM-YYYY"
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select className="form-control"
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}
              >
                <option>Active</option>
                <option>Inactive</option>
                <option>Under Maintenance</option>
              </select>
            </div>

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
      <button className="btn btn-primary" onClick={handleSave}>
        {editId ? "Update Pump Details" : "Save Pump"}
      </button>

            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
              ✕ Cancel
            </button>
          </div>

        </div>
      )}

      <div className="section-card">

        <div className="section-header">
          Pump Setup List — {setups.length} pumps configured
        </div>

        <div className="table-wrapper">

          <table className="data-table">

            <thead>
              <tr>
                
                <th>Pump No</th>
                <th>Pump Name</th>
                <th>Tank</th>
                <th>Product</th>
                <th>Meter Type</th>
                <th>Install Date</th>
                <th>Last Calibration</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>

              {setups.map((s, i) => (

                <tr key={s.id}>

                  

                  <td style={{ fontWeight: 700, color: 'var(--primary)' }}>
                    {s.pumpNo}
                  </td>

                  <td style={{ fontWeight: 600 }}>
                    {s.name}
                  </td>

                  <td>{s.tank}</td>

                  <td>
                    <span className={`badge ${
                      s.product === 'MS'
                        ? 'badge-danger'
                        : s.product === 'CNG'
                        ? 'badge-success'
                        : 'badge-info'
                    }`}>
                      {s.product}
                    </span>
                  </td>

                  <td>
                    <span className={`badge ${
                      s.meterType === 'Digital'
                        ? 'badge-primary'
                        : 'badge-warning'
                    }`}>
                      {s.meterType}
                    </span>
                  </td>

                  <td>{s.installDate}</td>

                  <td>{s.lastCalib}</td>

                  <td>
                    <span className={`badge ${
                      s.status === 'Active'
                        ? 'badge-success'
                        : s.status === 'Under Maintenance'
                        ? 'badge-warning'
                        : 'badge-danger'
                    }`}>
                      {s.status}
                    </span>
                  </td>

                  <td>

                    <button className="icon-btn" onClick={() => openEdit(s)}>
                      <i className="bi bi-pencil-square"></i>
                    </button>



                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}